import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { ChevronDown, X, Eye, EyeOff, Shield, BarChart3, Brain, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const StarField = () => {
  const canvasRef = React.useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const STAR_COUNT = 180;
    const stars = Array.from({ length: STAR_COUNT }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 1.8 + 0.2,
      speed: Math.random() * 0.35 + 0.05,
      opacity: Math.random() * 0.7 + 0.3,
      twinkleSpeed: Math.random() * 0.02 + 0.005,
      twinkleOffset: Math.random() * Math.PI * 2,
    }));

    let frame;
    let t = 0;
    const animate = () => {
      t += 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const star of stars) {
        star.y -= star.speed;
        star.x += Math.sin(t * 0.002 + star.twinkleOffset) * 0.15;
        if (star.y < -2) {
          star.y = canvas.height + 2;
          star.x = Math.random() * canvas.width;
        }
        if (star.x < -2) star.x = canvas.width + 2;
        if (star.x > canvas.width + 2) star.x = -2;

        const flicker = 0.5 + 0.5 * Math.sin(t * star.twinkleSpeed + star.twinkleOffset);
        const alpha = star.opacity * flicker;

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
        ctx.fill();

        // subtle glow for larger stars
        if (star.size > 1.2) {
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.size * 2.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(180, 210, 255, ${alpha * 0.15})`;
          ctx.fill();
        }
      }
      frame = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-0"
      style={{ pointerEvents: 'none' }}
    />
  );
};

const LandingPageInner = () => {
  const { signInWithEmail, signInWithGoogle, signInWithMicrosoft } = useAuth();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // v3: silently execute in the background, no user interaction needed
      const captchaToken = executeRecaptcha ? await executeRecaptcha('login') : null;
      await signInWithEmail(loginData.email, loginData.password, captchaToken);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setOauthLoading('google');
    try {
      await signInWithGoogle();
    } catch (error) {
      toast.error(error.message || 'Google login failed');
      setOauthLoading(null);
    }
  };

  const handleMicrosoftLogin = async () => {
    setOauthLoading('microsoft');
    try {
      await signInWithMicrosoft();
    } catch (error) {
      toast.error(error.message || 'Microsoft login failed');
      setOauthLoading(null);
    }
  };

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#030712]">
      {/* Navigation Bar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrollY > 50
          ? 'bg-[#030712]/90 backdrop-blur-xl border-b border-white/10'
          : 'bg-transparent'
      }`}>
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex-shrink-0">
            <img
              src="/vaia-logo-white.png"
              alt="VAIA"
              title="VAIA"
              className="h-10 w-auto"
            />
          </div>

          <div className="flex items-center gap-6 ml-auto">
            <ul className="hidden md:flex items-center gap-4">
              <li>
                <a href="https://in.linkedin.com/company/vaiaadvisory" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" title="LinkedIn">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                </a>
              </li>
              <li>
                <a href="https://www.instagram.com/team.vaia/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" title="Instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
              </li>
              <li>
                <a href="https://www.youtube.com/@VaiaLLP" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" title="YouTube">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              </li>
            </ul>

            <button
              onClick={() => setShowLoginModal(true)}
              className="px-6 py-2.5 bg-white text-gray-900 rounded-full font-semibold text-sm transition-all hover:shadow-lg hover:shadow-white/20 hover:scale-105"
            >
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Dark with animated gradient */}
      <div className="relative min-h-screen flex flex-col overflow-hidden">
        {/* Moving stars */}
        <StarField />

        {/* Animated gradient orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-1/4 w-[600px] h-[600px] rounded-full bg-ugmonk-yellow/10 blur-[128px] animate-pulse" style={{ animationDuration: '4s' }}></div>
          <div className="absolute bottom-1/4 -right-1/4 w-[600px] h-[600px] rounded-full bg-violet-600/20 blur-[128px] animate-pulse" style={{ animationDuration: '6s' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-cyan-500/10 blur-[100px] animate-pulse" style={{ animationDuration: '5s' }}></div>
        </div>

        {/* Grid pattern overlay */}
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}></div>

        {/* Content */}
        <div className="relative z-10 flex-1 flex items-center justify-center text-center px-6">
          <div className="max-w-5xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm mb-8">
              {/* <Sparkles size={14} className="text-cyan-400" /> */}
              <span className="text-sm text-gray-300">AI-Powered Wealth Management</span>
            </div>

            <h1 className="text-6xl md:text-8xl font-bold mb-4 tracking-tight" style={{ fontFamily: "'PerfectNineties', serif" }}>
              <span className="text-white">YOUR </span>
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400 bg-clip-text text-transparent">GOALS</span>
            </h1>
            <h2 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight" style={{ fontFamily: "'PerfectNineties', serif" }}>
              <span className="text-white">OUR </span>
              <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent">MISSION</span>
            </h2>

            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              Intelligent investment advisory that combines cutting-edge AI with
              personalized wealth management strategies.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setShowLoginModal(true)}
                className="group px-8 py-4 bg-white text-gray-900 rounded-full font-semibold text-lg transition-all hover:shadow-2xl hover:shadow-white/20 hover:scale-105 flex items-center gap-2"
              >
                Get Started
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={scrollToContent}
                className="px-8 py-4 border border-white/20 text-white rounded-full font-medium text-lg hover:bg-white/5 transition-all"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="relative z-10 pb-12 flex justify-center">
          <button onClick={scrollToContent} className="animate-bounce cursor-pointer">
            <ChevronDown size={32} className="text-gray-500" />
          </button>
        </div>
      </div>

      {/* Features Section - Dark cards */}
      <div className="relative py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-[#030712] via-[#0a0f1e] to-[#030712]"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'PerfectNineties', serif" }}>
              Intelligent by <span className="bg-gradient-to-r from-ugmonk-yellow to-[#f5cc6a] bg-clip-text text-transparent">Design</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Our platform empowers relationship managers and clients with data-driven insights and AI-powered recommendations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Card 1 */}
            <div className="group relative rounded-2xl p-8 border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] transition-all duration-500 hover:border-cyan-500/30">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-ugmonk-yellow to-[#f5cc6a] flex items-center justify-center mb-6 shadow-lg shadow-cyan-500/20">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Risk Profiling</h3>
                <p className="text-gray-400 leading-relaxed">
                  Comprehensive risk assessment with personalized investment recommendations tailored to your unique financial profile.
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="group relative rounded-2xl p-8 border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] transition-all duration-500 hover:border-violet-500/30">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-violet-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-6 shadow-lg shadow-violet-500/20">
                  <BarChart3 className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Portfolio Management</h3>
                <p className="text-gray-400 leading-relaxed">
                  Advanced analytics and AI-powered insights for optimal portfolio construction and rebalancing strategies.
                </p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="group relative rounded-2xl p-8 border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] transition-all duration-500 hover:border-fuchsia-500/30">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-fuchsia-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-600 flex items-center justify-center mb-6 shadow-lg shadow-fuchsia-500/20">
                  <Brain className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Intelligent Reports</h3>
                <p className="text-gray-400 leading-relaxed">
                  Generate comprehensive investment proposals and performance reports powered by advanced AI analysis.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="relative py-20">
        <div className="absolute inset-0 bg-[#030712]"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '99.9%', label: 'Uptime' },
              { value: '500+', label: 'Portfolios Managed' },
              { value: '24/7', label: 'AI Assistance' },
              { value: '100%', label: 'Data Security' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-violet-400 bg-clip-text text-transparent mb-2" style={{ fontFamily: "'PerfectNineties', serif" }}>
                  {stat.value}
                </div>
                <div className="text-sm text-gray-500 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[#030712]"></div>
        <StarField />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-ugmonk-yellow/5 blur-[120px]"></div>
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-violet-600/10 blur-[120px]"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'PerfectNineties', serif" }}>
            Ready to Transform Your
            <br />
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400 bg-clip-text text-transparent">Investment Strategy?</span>
          </h2>
          <p className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto">
            Join us today and experience the future of personalized wealth management powered by artificial intelligence.
          </p>
          <button
            onClick={() => setShowLoginModal(true)}
            className="group px-10 py-4 bg-gradient-to-r from-ugmonk-yellow to-[#f5cc6a] text-white rounded-full font-semibold text-lg transition-all hover:shadow-2xl hover:shadow-ugmonk-yellow/30 hover:scale-105 flex items-center gap-2 mx-auto"
          >
            Get Started Today
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 bg-[#030712]">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img src="/vaia-logo-white.png" alt="VAIA" className="h-8 w-auto opacity-60" />
            <p className="text-sm text-gray-600">&copy; 2025 VAIA Wealth Management. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
          <div className="relative bg-[#0f1629] rounded-2xl shadow-2xl max-w-md w-full border border-white/10 animate-in fade-in zoom-in duration-200">
            {/* Gradient glow behind modal */}
            <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-violet-500/20 rounded-2xl blur-xl opacity-50"></div>

            <div className="relative">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <div>
                  <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'PerfectNineties', serif" }}>
                    Welcome Back
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">Sign in to continue to your dashboard</p>
                </div>
                <button
                  onClick={() => setShowLoginModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-400" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                {/* OAuth Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={handleGoogleLogin}
                    disabled={oauthLoading === 'google' || loading}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-white/10 rounded-xl hover:bg-white/5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed bg-white/[0.02]"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    <span className="text-gray-300 font-medium">
                      {oauthLoading === 'google' ? 'Redirecting...' : 'Continue with Google'}
                    </span>
                  </button>

                  <button
                    onClick={handleMicrosoftLogin}
                    disabled={oauthLoading === 'microsoft' || loading}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-white/10 rounded-xl hover:bg-white/5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed bg-white/[0.02]"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 21 21">
                      <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                      <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                      <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                    </svg>
                    <span className="text-gray-300 font-medium">
                      {oauthLoading === 'microsoft' ? 'Redirecting...' : 'Continue with Microsoft'}
                    </span>
                  </button>
                </div>

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-4 text-sm bg-[#0f1629] text-gray-500">OR</span>
                  </div>
                </div>

                {/* Email/Password Form */}
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-300">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@vaiaclimate.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      disabled={loading}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-cyan-500/50 focus:ring-cyan-500/20"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-gray-300">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        disabled={loading}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-cyan-500/50 focus:ring-cyan-500/20 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black font-semibold py-3 mt-6 rounded-xl shadow-lg shadow-ugmonk-yellow/20"
                    disabled={loading || oauthLoading}
                  >
                    {loading ? 'Signing in...' : 'Sign in'}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-sm text-gray-500 text-center">
                    Don't have an account? <a href="#" onClick={(e) => e.preventDefault()} className="text-cyan-400 font-semibold hover:underline">Contact us</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const LandingPage = () => (
  <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
    <LandingPageInner />
  </GoogleReCaptchaProvider>
);

export default LandingPage;
